class Hero30BackgroundSlider {
  constructor(container) {
    this.container = container;
    this.backgrounds = Array.from(container.querySelectorAll('[data-background-index]'));
    this.prevButtons = Array.from(container.querySelectorAll('.hero-3-0__arrow--prev'));
    this.nextButtons = Array.from(container.querySelectorAll('.hero-3-0__arrow--next'));

    this.currentIndex = 0;
    this.desktopMediaQuery = window.matchMedia('(min-width: 768px)');
    this.autoloopDesktop = container.dataset.autoloop === 'true';
    this.autoloopMobile = container.dataset.autoloopMobile
      ? container.dataset.autoloopMobile === 'true'
      : this.autoloopDesktop;
    this.scrollTimeDesktop = this.normalizeScrollTime(container.dataset.scrollTime, 5000);
    this.scrollTimeMobile = this.normalizeScrollTime(container.dataset.scrollTimeMobile, this.scrollTimeDesktop);
    this.autoloop = this.getCurrentAutoloop();
    this.scrollTime = this.getCurrentScrollTime();
    this.timer = null;
    this.isTransitioning = false;
    this.textFadeDuration = 170;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.textAnimationToken = 0;

    this.init();
  }

  normalizeScrollTime(value, fallback) {
    const parsed = parseInt(value, 10);

    if (Number.isNaN(parsed)) {
      return Math.max(1000, fallback);
    }

    return Math.max(1000, parsed);
  }

  isDesktopView() {
    return this.desktopMediaQuery.matches;
  }

  getCurrentAutoloop() {
    return this.isDesktopView() ? this.autoloopDesktop : this.autoloopMobile;
  }

  getCurrentScrollTime() {
    return this.isDesktopView() ? this.scrollTimeDesktop : this.scrollTimeMobile;
  }

  getTemplateHtml(scope, selector) {
    if (!scope) {
      return '';
    }

    const sourceElement = scope.querySelector(selector);
    return sourceElement ? sourceElement.innerHTML.trim() : '';
  }

  initTextElements() {
    const styleClasses = ['h1', 'h2', 'h3', 'h4', 'copy1'];
    const globalTemplates = {};
    const result = {};

    this.container.querySelectorAll('[data-hero-global-template]').forEach((templateEl) => {
      globalTemplates[templateEl.dataset.heroGlobalTemplate] = templateEl.innerHTML.trim();
    });

    this.container.querySelectorAll('[data-hero-text]').forEach((el) => {
      const key = el.dataset.heroText;
      const initialHidden = el.classList.contains('hidden');
      result[key] = {
        el,
        globalContent: globalTemplates[key] || '',
        globalTag: el.dataset.globalTag || '',
        globalStyle: el.dataset.globalStyle || '',
        globalEmpty: el.dataset.globalEmpty === '1',
        baseClass: el.dataset.baseClass || el.className,
        styleClasses,
        lastContent: (el.innerHTML || '').trim(),
        lastTag: el.tagName.toLowerCase(),
        lastStyle: '',
        lastSourceType: 'initial',
        lastHidden: initialHidden,
      };
    });

    return result;
  }

  initButtonsElement() {
    const el = this.container.querySelector('[data-hero-buttons]');

    if (!el) {
      return null;
    }

    return {
      el,
      globalHtml: this.getTemplateHtml(this.container, '[data-hero-global-template="buttons"]'),
    };
  }

  replaceElementTag(entry, nextTag) {
    if (!entry || !entry.el || !nextTag) {
      return entry?.el || null;
    }

    const currentTag = entry.el.tagName.toLowerCase();

    if (currentTag === nextTag) {
      return entry.el;
    }

    const newElement = document.createElement(nextTag);

    Array.from(entry.el.attributes).forEach((attr) => {
      newElement.setAttribute(attr.name, attr.value);
    });

    newElement.className = entry.el.className;
    newElement.innerHTML = entry.el.innerHTML;
    entry.el.replaceWith(newElement);
    entry.el = newElement;

    return newElement;
  }

  computeTextState(entry, config) {
    if (!entry) {
      return null;
    }

    const hasOverride = Boolean(config.overrideContent && config.overrideContent.trim());
    const hasGlobal = Boolean(entry.globalContent && entry.globalContent.trim());
    const useExclusiveSlideContent = Boolean(config.useExclusiveSlideContent);
    const sourceType = hasOverride ? 'override' : (hasGlobal ? 'global' : 'empty');

    let nextContent = '';
    let nextTag = entry.globalTag || entry.lastTag || '';
    let nextStyle = entry.globalStyle || entry.lastStyle || '';
    let shouldHide = false;

    if (hasOverride) {
      nextContent = config.overrideContent;
      nextTag = config.overrideTag || entry.globalTag || entry.lastTag || '';
      nextStyle = config.overrideStyle || entry.globalStyle || entry.lastStyle || '';
    } else if (useExclusiveSlideContent) {
      shouldHide = true;
    } else if (hasGlobal) {
      nextContent = entry.globalContent;
      nextTag = entry.globalTag || entry.lastTag || '';
      nextStyle = entry.globalStyle || entry.lastStyle || '';
    } else {
      shouldHide = true;
    }

    const globalContent = (entry.globalContent || '').trim();
    const globalTag = entry.globalTag || '';
    const globalStyle = entry.globalStyle || '';
    const isDifferentFromGlobal = hasOverride && (
      (nextContent || '').trim() !== globalContent
      || (nextTag || '') !== globalTag
      || (nextStyle || '') !== globalStyle
    );

    const normalizedNextContent = (nextContent || '').trim();
    const normalizedLastContent = (entry.lastContent || '').trim();
    const hasChanged = (
      normalizedNextContent !== normalizedLastContent
      || (nextTag || '') !== (entry.lastTag || '')
      || (nextStyle || '') !== (entry.lastStyle || '')
      || sourceType !== (entry.lastSourceType || 'initial')
      || shouldHide !== Boolean(entry.lastHidden)
    );

    return {
      hasOverride,
      isDifferentFromGlobal,
      hasChanged,
      sourceType,
      nextContent,
      nextTag,
      nextStyle,
      shouldHide,
    };
  }

  applyTextEntry(entry, state) {
    if (!entry) {
      return null;
    }

    if (state.nextTag) {
      this.replaceElementTag(entry, state.nextTag);
    }

    const { el } = entry;
    el.innerHTML = state.nextContent || '';
    el.className = entry.baseClass;

    entry.styleClasses.forEach((styleClass) => {
      el.classList.remove(styleClass);
    });

    if (state.nextStyle) {
      el.classList.add(state.nextStyle);
    }

    if (state.shouldHide) {
      el.classList.add('hidden');
      entry.lastHidden = true;
      entry.lastSourceType = state.sourceType;
    } else {
      el.classList.remove('hidden');
      entry.lastContent = state.nextContent;
      entry.lastTag = el.tagName.toLowerCase();
      entry.lastStyle = state.nextStyle;
      entry.lastSourceType = state.sourceType;
      entry.lastHidden = false;
    }

    return el;
  }

  updateSlideText(slideEl, animateOverrides = false) {
    if (!this.textElements) {
      return;
    }

    const useExclusiveSlideContent = slideEl?.dataset.slideHasCustomContent === 'true';

    const head1Content = this.getTemplateHtml(slideEl, '.hero-3-0__slide-head1-template[aria-hidden="true"]');
    const head2Content = this.getTemplateHtml(slideEl, '.hero-3-0__slide-head2-template[aria-hidden="true"]');
    const descContent = this.getTemplateHtml(slideEl, '.hero-3-0__slide-desc-template[aria-hidden="true"]');

    const head1State = this.computeTextState(this.textElements.head1, {
      overrideContent: head1Content,
      overrideTag: slideEl?.dataset.slideHead1Tag || '',
      overrideStyle: slideEl?.dataset.slideHead1Style || '',
      useExclusiveSlideContent,
    });

    const head2State = this.computeTextState(this.textElements.head2, {
      overrideContent: head2Content,
      overrideTag: slideEl?.dataset.slideHead2Tag || '',
      overrideStyle: slideEl?.dataset.slideHead2Style || '',
      useExclusiveSlideContent,
    });

    const descState = this.computeTextState(this.textElements.desc, {
      overrideContent: descContent,
      overrideTag: '',
      overrideStyle: '',
      useExclusiveSlideContent,
    });

    const states = [
      [this.textElements.head1, head1State],
      [this.textElements.head2, head2State],
      [this.textElements.desc, descState],
    ];

    const shouldAnimateGroup = animateOverrides
      && !this.prefersReducedMotion
      && states.some(([, state]) => state && state.hasOverride && state.hasChanged && !state.shouldHide);

    this.textAnimationToken += 1;
    const animationToken = this.textAnimationToken;

    if (!shouldAnimateGroup) {
      states.forEach(([entry, state]) => {
        if (!entry || !state) {
          return;
        }

        const updatedElement = this.applyTextEntry(entry, state);
        updatedElement?.classList?.remove('hero-3-0__text--fade-out', 'hero-3-0__text--fade-in');
      });
      return;
    }

    states.forEach(([entry]) => {
      if (entry?.el && !entry.el.classList.contains('hidden')) {
        entry.el.classList.remove('hero-3-0__text--fade-in');
        entry.el.classList.add('hero-3-0__text--fade-out');
      }
    });

    window.setTimeout(() => {
      if (animationToken !== this.textAnimationToken) {
        return;
      }

      const visibleElements = [];

      states.forEach(([entry, state]) => {
        if (!entry || !state) {
          return;
        }

        const updatedElement = this.applyTextEntry(entry, state);
        updatedElement?.classList?.remove('hero-3-0__text--fade-out');

        if (updatedElement && !updatedElement.classList.contains('hidden')) {
          updatedElement.classList.add('hero-3-0__text--fade-in');
          visibleElements.push(updatedElement);
        }
      });

      window.setTimeout(() => {
        if (animationToken !== this.textAnimationToken) {
          return;
        }

        visibleElements.forEach((element) => {
          element.classList.remove('hero-3-0__text--fade-in');
        });
      }, this.textFadeDuration);
    }, this.textFadeDuration);
  }

  updateSlideButtons(slideEl) {
    if (!this.buttonsElement) {
      return;
    }

    const useExclusiveSlideContent = slideEl?.dataset.slideHasCustomContent === 'true';
    const slideButtonsHtml = this.getTemplateHtml(slideEl, '.hero-3-0__slide-buttons-template[aria-hidden="true"]');
    const nextHtml = useExclusiveSlideContent
      ? slideButtonsHtml
      : (slideButtonsHtml || this.buttonsElement.globalHtml);

    this.buttonsElement.el.innerHTML = nextHtml;

    if (nextHtml) {
      this.buttonsElement.el.classList.remove('hidden');
    } else {
      this.buttonsElement.el.classList.add('hidden');
    }
  }

  applySlideContent(slideEl, animateOverrides = false) {
    this.updateSlideText(slideEl, animateOverrides);
    this.updateSlideButtons(slideEl);
  }

  init() {
    this.textElements = this.initTextElements();
    this.buttonsElement = this.initButtonsElement();
    this.applySlideContent(this.backgrounds[0] || null);

    if (this.backgrounds.length <= 1) {
      return;
    }

    this.prevButtons.forEach((button) => {
      button.addEventListener('click', () => this.goToPrev());
    });

    this.nextButtons.forEach((button) => {
      button.addEventListener('click', () => this.goToNext());
    });

    if (this.autoloop) {
      this.startAutoloop();

      this.container.addEventListener('mouseenter', () => this.stopAutoloop());
      this.container.addEventListener('mouseleave', () => this.startAutoloop());
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoloop();
      } else if (this.autoloop) {
        this.startAutoloop();
      }
    });

    window.addEventListener('resize', () => this.syncViewportSettings());
  }

  syncViewportSettings() {
    const nextAutoloop = this.getCurrentAutoloop();
    const nextScrollTime = this.getCurrentScrollTime();
    const settingsChanged = this.autoloop !== nextAutoloop || this.scrollTime !== nextScrollTime;

    if (!settingsChanged) {
      return;
    }

    this.autoloop = nextAutoloop;
    this.scrollTime = nextScrollTime;

    if (this.autoloop) {
      this.startAutoloop();
    } else {
      this.stopAutoloop();
    }
  }

  goToNext() {
    if (this.isTransitioning) {
      return;
    }

    const nextIndex = (this.currentIndex + 1) % this.backgrounds.length;
    this.goToSlide(nextIndex);
  }

  goToPrev() {
    if (this.isTransitioning) {
      return;
    }

    const prevIndex = (this.currentIndex - 1 + this.backgrounds.length) % this.backgrounds.length;
    this.goToSlide(prevIndex);
  }

  goToSlide(index) {
    if (index === this.currentIndex || this.isTransitioning) {
      return;
    }

    const currentSlide = this.backgrounds[this.currentIndex];
    const nextSlide = this.backgrounds[index];

    if (!currentSlide || !nextSlide) {
      return;
    }

    this.isTransitioning = true;

    currentSlide.classList.remove('opacity-100');
    currentSlide.classList.add('opacity-0', 'pointer-events-none');

    nextSlide.classList.remove('opacity-0', 'pointer-events-none');
    nextSlide.classList.add('opacity-100');

    this.currentIndex = index;
    this.applySlideContent(nextSlide, true);

    window.setTimeout(() => {
      this.isTransitioning = false;
    }, 700);

    if (this.autoloop) {
      this.stopAutoloop();
      this.startAutoloop();
    }
  }

  startAutoloop() {
    this.stopAutoloop();
    this.timer = setInterval(() => {
      this.goToNext();
    }, this.scrollTime);
  }

  stopAutoloop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

const initHero30 = () => {
  const containers = document.querySelectorAll('.hero-3-0__container');

  containers.forEach((container) => {
    if (container.dataset.hero30Initialized === 'true') {
      return;
    }

    new Hero30BackgroundSlider(container);
    container.dataset.hero30Initialized = 'true';
  });
};

export default initHero30;
